import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getMemberExpandKey } from '../utils/dashboard/memberTreeHelpers';
import { getMemberDisplayName, missionIdMatchesMember, looksLikeGhostMissionId, getCanonicalMemberId } from '../utils/dashboard/deviceIdentity';
import { getMemberListDisplayName, memberNoteKey } from '../utils/dashboard/commanderMemberNoteHelpers';
import MemberNoteEditorModal from './MemberNoteEditorModal';
import { PRESENCE_STATUS, PRESENCE_DOT_COLORS } from '../utils/dashboard/constants';
import MissionMemberChatWindow, { VoiceIconButton } from './MissionMemberChatWindow';
import { canEditMissionBoard, hasCommandFeature, COMMAND_FEATURE_PERMS } from '../utils/permissionControl';

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#00838f', '#c62828'];

function memberIdOf(m) {
  return getMemberExpandKey(m);
}

function presenceOf(member, getPresence) {
  if (typeof getPresence === 'function') {
    const s = getPresence(member);
    if (s) return s;
  }
  return PRESENCE_STATUS.OFFLINE;
}

function presenceDotColor(status) {
  if (status === PRESENCE_STATUS.ONLINE || status === 'online') return PRESENCE_DOT_COLORS.online || '#2ea8ff';
  if (status === PRESENCE_STATUS.TEMP_OFFLINE || status === 'temp_offline') return PRESENCE_DOT_COLORS.temp_offline || '#f59e0b';
  return PRESENCE_DOT_COLORS.offline || '#8a93a0';
}

function presenceLabel(status) {
  if (status === PRESENCE_STATUS.ONLINE || status === 'online') return '在线';
  if (status === PRESENCE_STATUS.TEMP_OFFLINE || status === 'temp_offline') return '暂时离开';
  return '离线';
}

function formatMemberSeen(lastSeen) {
  if (!lastSeen) return null;
  const d = new Date(lastSeen);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  const pad = (n) => String(n).padStart(2, '0');
  if (sameDay) {
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())} 更新`;
  }
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())} 更新`;
}

function formatFullSeen(lastSeen) {
  if (!lastSeen) return null;
  const d = new Date(lastSeen);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `更新于 ${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function fmtMetric(v, unit = '', digits = null) {
  if (v == null || v === '') return '未知';
  const n = Number(v);
  if (!Number.isFinite(n)) return '未知';
  const text = digits != null ? n.toFixed(digits) : String(n);
  return `${text}${unit}`;
}

function buildMemberInfoLines(member, status, notesByKey = null) {
  const name = getMemberListDisplayName(member, notesByKey) || memberIdOf(member) || '未知';
  const batteryText = member?.battery_level != null ? `电量 ${member.battery_level}%` : null;
  const seenText = formatMemberSeen(member?.last_seen);
  return [
    name,
    presenceLabel(status),
    batteryText,
    seenText,
    member?.org_name ? `组织 ${member.org_name}` : null,
    member?.group_name ? `小组 ${member.group_name}` : null,
    member?.device_unique_id ? `设备 ${member.device_unique_id}` : null,
    member?.name && member.name !== name ? `标识 ${member.name}` : null
  ].filter(Boolean);
}


/** 小号拖拽预览：避免浏览器默认拖影因祖先层合成而克隆整块侧栏面板 */
function createMissionNameDragImage(label, status) {
  const ghost = document.createElement('div');
  ghost.setAttribute('data-mission-drag-ghost', '1');
  Object.assign(ghost.style, {
    position: 'fixed',
    top: '-1000px',
    left: '-1000px',
    zIndex: '99999',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    height: '26px',
    maxWidth: '160px',
    padding: '0 8px',
    borderRadius: '6px',
    border: '1px solid #e6ebf0',
    background: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
    fontSize: '12px',
    fontWeight: '600',
    color: '#222',
    fontFamily: 'inherit',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  });
  const dot = document.createElement('span');
  Object.assign(dot.style, {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: presenceDotColor(status),
    flexShrink: '0'
  });
  const text = document.createElement('span');
  text.textContent = label || '队员';
  Object.assign(text.style, {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '120px'
  });
  ghost.appendChild(dot);
  ghost.appendChild(text);
  document.body.appendChild(ghost);
  return ghost;
}

function attachMissionNameDragImage(dataTransfer, label, status) {
  if (!dataTransfer || typeof dataTransfer.setDragImage !== 'function') return;
  try {
    const ghost = createMissionNameDragImage(label, status);
    dataTransfer.setDragImage(ghost, 12, 13);
    requestAnimationFrame(() => {
      setTimeout(() => {
        try { ghost.remove(); } catch (_) { /* ignore */ }
      }, 0);
    });
  } catch (_) { /* ignore */ }
}

function stubMemberFromId(id) {
  if (!id) return null;
  const parts = String(id).split(':');
  const short = parts[parts.length - 1] || id;
  return {
    name: short,
    device_unique_id: id,
    __missionStub: true
  };
}

function NameChip({
  member,
  status = PRESENCE_STATUS.OFFLINE,
  draggable = true,
  onDragStart,
  onRemove,
  showVoice = false,
  voicePulsing = false,
  onVoiceClick,
  onFlyTo = null,
  compact = false,
  notesByKey = null,
  onOpenCommandMenu = null
}) {
  const [hover, setHover] = useState(false);
  const name = getMemberListDisplayName(member, notesByKey) || memberIdOf(member) || '未知';
  const infoLines = buildMemberInfoLines(member, status, notesByKey);
  const tipRef = useRef(null);

  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        setHover(false);
        onDragStart?.(e);
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: compact ? 26 : 28,
        maxWidth: '100%',
        padding: '0 8px',
        borderRadius: 6,
        border: '1px solid #e6ebf0',
        background: '#fff',
        cursor: draggable ? 'grab' : 'default',
        fontSize: 12,
        color: '#333',
        userSelect: 'none',
        verticalAlign: 'top'
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: presenceDotColor(status),
          flexShrink: 0
        }}
        title={presenceLabel(status)}
      />
      <span
        role={(onOpenCommandMenu || onFlyTo) ? 'button' : undefined}
        title={onOpenCommandMenu ? '点击打开菜单' : (onFlyTo ? '点击定位到地图' : undefined)}
        onClick={(e) => {
          e.stopPropagation();
          if (onOpenCommandMenu) {
            onOpenCommandMenu(member, e.currentTarget);
            return;
          }
          if (!onFlyTo) return;
          onFlyTo(member);
        }}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: 600,
          color: '#222',
          maxWidth: 120,
          cursor: (onOpenCommandMenu || onFlyTo) ? 'pointer' : 'inherit'
        }}
      >
        {name}
      </span>
      {showVoice ? (
        <VoiceIconButton
          unread={voicePulsing}
          onClick={() => onVoiceClick?.(member)}
        />
      ) : null}
      {status !== PRESENCE_STATUS.ONLINE && status !== 'online' ? (
        <span style={{ fontSize: 10, color: '#8a93a0', flexShrink: 0 }}>
          {presenceLabel(status)}
        </span>
      ) : null}
      {onRemove ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#999',
            cursor: 'pointer',
            fontSize: 14,
            lineHeight: 1,
            padding: 0,
            flexShrink: 0
          }}
          title="移出任务"
        >
          ×
        </button>
      ) : null}
      {hover ? (
        <div
          ref={tipRef}
          style={{
            position: 'absolute',
            left: 0,
            bottom: '100%',
            marginBottom: 4,
            zIndex: 40,
            minWidth: 180,
            maxWidth: 280,
            padding: '8px 10px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
            fontSize: 11,
            lineHeight: 1.55,
            color: '#333',
            whiteSpace: 'pre-line',
            pointerEvents: 'none'
          }}
        >
          {infoLines.join('\n')}
        </div>
      ) : null}
    </div>
  );
}

function GroupMemberDetail({ member, status }) {
  const cell = (label, value) => (
    <div style={{ minWidth: 0 }}>
      <span style={{ color: '#888' }}>{label}</span>
      <span style={{ marginLeft: 4, color: '#222', fontWeight: 500 }}>{value}</span>
    </div>
  );
  const fullSeen = formatFullSeen(member?.last_seen);
  return (
    <div
      style={{
        padding: '10px 12px',
        background: '#f7fafc',
        fontSize: 12,
        borderTop: '1px solid #e6edf2'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {fullSeen ? (
        <div style={{ marginBottom: 8, color: '#666', fontSize: 11 }}>{fullSeen}</div>
      ) : null}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '6px 12px',
        marginBottom: 8
      }}
      >
        {cell('状态', presenceLabel(status))}
        {cell('电量', member?.battery_level != null && member.battery_level !== '' ? `${member.battery_level}%` : '未知')}
        {cell('经度', member?.last_longitude != null ? Number(member.last_longitude).toFixed(6) : '未知')}
        {cell('纬度', member?.last_latitude != null ? Number(member.last_latitude).toFixed(6) : '未知')}
        {cell('海拔', member?.last_altitude != null ? `${member.last_altitude} m` : '未知')}
        {cell('精度', fmtMetric(member?.last_accuracy, ' m'))}
        {cell('速度', fmtMetric(member?.last_speed, ' m/s', 1))}
        {cell('方向', fmtMetric(member?.last_bearing, '°', 1))}
        {cell('信号', member?.gsm != null && member.gsm !== '' ? member.gsm : '未知')}
        {cell('组织', member?.org_name || '未知')}
        {cell('小组', member?.group_name || '未知')}
        {cell('设备', member?.device_unique_id || member?.name || '未知')}
      </div>
    </div>
  );
}

/**
 * 任务分组内队员行：与「全部」列表同类信息行，默认可收起，不因上线自动展开。
 */
function GroupMemberRow({
  member,
  status = PRESENCE_STATUS.OFFLINE,
  expanded = false,
  onToggleExpand,
  draggable = true,
  onDragStart,
  onRemove,
  showVoice = false,
  voicePulsing = false,
  onVoiceClick,
  isStreaming = false,
  onOpenLiveStream = null,
  onFlyTo = null,
  notesByKey = null,
  onOpenCommandMenu = null
}) {
  const name = getMemberListDisplayName(member, notesByKey) || memberIdOf(member) || '未知';
  const batteryText = member?.battery_level != null ? `电量 ${member.battery_level}%` : null;
  const seenText = formatMemberSeen(member?.last_seen);
  const tip = buildMemberInfoLines(member, status, notesByKey).join('\n');

  return (
    <div
      style={{
        border: '1px solid ' + (expanded ? '#b5d0ea' : '#e9ecef'),
        borderRadius: 8,
        background: expanded ? '#e8f1fb' : '#fff',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <div
        draggable={draggable}
        onDragStart={onDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 8px',
          minHeight: 34,
          cursor: draggable ? 'grab' : 'default',
          userSelect: 'none'
        }}
      >
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand?.();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleExpand?.();
            }
          }}
          title={expanded ? '收起' : '展开详情'}
          style={{
            fontSize: 12,
            cursor: 'pointer',
            color: '#1976d2',
            userSelect: 'none',
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            display: 'inline-flex',
            width: 14,
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          ▶
        </span>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: presenceDotColor(status),
            flexShrink: 0
          }}
          title={presenceLabel(status)}
        />
        <span
          role={(onOpenCommandMenu || onFlyTo) ? 'button' : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (onOpenCommandMenu) {
              onOpenCommandMenu(member, e.currentTarget);
              return;
            }
            if (!onFlyTo) return;
            onFlyTo(member);
          }}
          style={{
            fontWeight: 600,
            flex: '0 1 auto',
            minWidth: 0,
            maxWidth: '42%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: '#222',
            fontSize: 12,
            cursor: (onOpenCommandMenu || onFlyTo) ? 'pointer' : 'inherit'
          }}
          title={onOpenCommandMenu ? (tip + '\n点击打开菜单') : (onFlyTo ? (tip + '\n点击定位到地图') : tip)}
        >
          {name}
        </span>
        {showVoice ? (
          <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', marginLeft: 2 }}>
            <VoiceIconButton
              unread={voicePulsing}
              onClick={() => onVoiceClick?.(member)}
            />
          </span>
        ) : null}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 6,
            flex: '1 1 auto',
            minWidth: 0,
            marginLeft: 'auto',
            overflow: 'hidden',
            fontSize: 11,
            color: '#666',
            whiteSpace: 'nowrap'
          }}
          title={[presenceLabel(status), seenText, batteryText].filter(Boolean).join(' | ')}
        >
          <span style={{ fontSize: 10, color: (status === PRESENCE_STATUS.ONLINE || status === 'online') ? '#2e7d32' : '#8a93a0', flexShrink: 0 }}>
            {presenceLabel(status)}
          </span>
          {seenText ? (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{seenText}</span>
          ) : null}
          {batteryText ? (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0, color: '#555' }}>
              {batteryText}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!isStreaming) return;
            onOpenLiveStream?.(member);
          }}
          title={isStreaming ? '观看前线直播' : '直播未在线'}
          aria-label={isStreaming ? '观看直播' : '直播未在线'}
          style={{
            flexShrink: 0,
            height: 22,
            minWidth: 40,
            padding: '0 8px',
            borderRadius: 4,
            border: isStreaming ? 'none' : '1px solid #d0d5dd',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.5,
            cursor: isStreaming ? 'pointer' : 'default',
            background: isStreaming ? '#e53935' : '#eceff1',
            color: isStreaming ? '#fff' : '#90a4ae',
            lineHeight: '22px'
          }}
        >
          直播
        </button>
        {onRemove ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#999',
              cursor: 'pointer',
              fontSize: 14,
              lineHeight: 1,
              padding: 0,
              flexShrink: 0
            }}
            title="移出本组"
          >
            ×
          </button>
        ) : null}
      </div>
      {expanded ? <GroupMemberDetail member={member} status={status} /> : null}
    </div>
  );
}

/**
 * Mission kanban: pending pool (top) + task groups (bottom).
 * Grouped members stay put when offline; only manual remove / end mission clears them.
 */
export default function MissionKanbanPanel({
  missionMembers = [],
  missionGroups = [],
  onGroupsChange,
  onRemoveMember,
  onEndMission,
  endMissionEnabled = null,
  getMemberPresence,
  voicePulseIds = null,
  streamingMembers = null,
  openLiveStream = null,
  getMessagesForMember,
  onOutboundSent,
  onChatOpenChange,
  getMap = null,
  getBasemapId = null,
  onMessageDeleted = null,
  historyEpoch = 0,
  onFlyToMember = null,
  notesByKey = null,
  onSaveMemberNote = null,
  onSendLocationToMember = null,
  onSendMessageToMember = null
}) {
  const canEditBoard = canEditMissionBoard();
  const canEditNotes = hasCommandFeature(COMMAND_FEATURE_PERMS.MEMBER_NOTES);
  const [commandMenu, setCommandMenu] = useState(null);
  const [noteEditor, setNoteEditor] = useState(null);
  const commandMenuRef = useRef(null);
  const closeCommandMenu = () => setCommandMenu(null);
  const openCommandMenuFor = (member, anchorEl) => {
    const key = member?.device_unique_id || member?.name;
    const rect = anchorEl?.getBoundingClientRect?.();
    if (!rect || !key) return;
    setCommandMenu((prev) => {
      if (prev?.key === key) return null;
      const estimatedH = 220;
      const top = rect.bottom + 4 + estimatedH > window.innerHeight
        ? Math.max(8, rect.top - estimatedH - 4)
        : rect.bottom + 4;
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - 140);
      return { key, member, top, left };
    });
  };
  useEffect(() => {
    if (!commandMenu) return undefined;
    const onDoc = (e) => {
      if (commandMenuRef.current && commandMenuRef.current.contains(e.target)) return;
      closeCommandMenu();
    };
    const onKey = (e) => { if (e.key === 'Escape') closeCommandMenu(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [commandMenu]);
  const [dragPayload, setDragPayload] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [poolCollapsed, setPoolCollapsed] = useState(false);
  const [chatMemberId, setChatMemberId] = useState(null);
  // 任务分组内详情展开；默认空 = 上线不自动展开
  const [expandedMemberIds, setExpandedMemberIds] = useState(() => ({}));

  const memberById = useMemo(() => {
    const map = {};
    (missionMembers || []).forEach((m) => {
      const id = memberIdOf(m);
      if (id) map[id] = m;
    });
    return map;
  }, [missionMembers]);

  const groupedIds = useMemo(() => {
    const set = new Set();
    (missionGroups || []).forEach((g) => {
      (g.memberIds || []).forEach((id) => set.add(id));
    });
    return set;
  }, [missionGroups]);

  const poolMembers = useMemo(
    () => (missionMembers || []).filter((m) => {
      const id = memberIdOf(m);
      if (!id) return false;
      if (groupedIds.has(id)) return false;
      // fuzzy: group may store bind-token id while live row is device id
      for (const gid of groupedIds) {
        if (missionIdMatchesMember(gid, m)) return false;
      }
      return true;
    }),
    [missionMembers, groupedIds]
  );

  const resolveMember = (id) => {
    if (!id) return null;
    const accept = (m) => {
      if (!m) return null;
      // Stub：仅藏长 hex 令牌幽灵；真人名册行一律保留（含 leidian14A 等设备名）
      if (m.__missionStub) {
        if (looksLikeGhostMissionId(id) || looksLikeGhostMissionId(m.device_unique_id)) {
          return null;
        }
        return m;
      }
      // 名册里的行 = 可拖可编组；不再用 memberHasHumanDisplayName 过滤（否则拖入分组后消失）
      return m;
    };
    if (memberById[id]) return accept(memberById[id]);
    const fuzzy = (missionMembers || []).find((m) => missionIdMatchesMember(id, m));
    if (fuzzy) return accept(fuzzy);
    // Never promote raw bind/stream tokens into selectable/display rows
    if (looksLikeGhostMissionId(id)) return null;
    return accept(stubMemberFromId(id));
  };

  /** 任务组内多个历史 id（名字/uid/zhxt_app）可能指向同一人：渲染前按 live key 去重 */
  const resolveGroupMembers = (ids) => {
    const out = [];
    const seen = new Set();
    (ids || []).forEach((storedId) => {
      const m = resolveMember(storedId);
      if (!m) return;
      const liveKey = memberIdOf(m) || String(storedId || '').trim();
      if (!liveKey || seen.has(liveKey)) return;
      seen.add(liveKey);
      out.push({ member: m, storedId: liveKey });
    });
    return out;
  };

  const setGroups = (next) => {
    if (!canEditBoard) return;
    onGroupsChange?.(typeof next === 'function' ? next(missionGroups || []) : next);
  };

  const beginDrag = (memberId, from) => (e) => {
    if (!canEditBoard) {
      e.preventDefault();
      return;
    }
    setDragPayload({ memberId, from });
    try {
      e.dataTransfer.setData('text/plain', JSON.stringify({ memberId, from }));
      e.dataTransfer.effectAllowed = 'move';
      const member = resolveMember(memberId);
      const label = getMemberDisplayName(member) || memberIdOf(member) || String(memberId || '队员');
      const status = presenceOf(member, getMemberPresence);
      attachMissionNameDragImage(e.dataTransfer, label, status);
    } catch (_) { /* ignore */ }
  };

  const readDrag = (e) => {
    try {
      const raw = e.dataTransfer.getData('text/plain');
      if (raw) return JSON.parse(raw);
    } catch (_) { /* ignore */ }
    return dragPayload;
  };

  const moveMember = (memberId, to) => {
    if (!memberId) return;
    setGroups((prev) => {
      const cleaned = (prev || []).map((g) => ({
        ...g,
        memberIds: (g.memberIds || []).filter((id) => !(
          id === memberId
          || missionIdMatchesMember(memberId, { device_unique_id: id, name: id })
          || missionIdMatchesMember(id, { device_unique_id: memberId, name: memberId })
        ))
      }));
      if (to === 'pool') return cleaned;
      return cleaned.map((g) => {
        if (g.id !== to) return g;
        const ids = g.memberIds || [];
        const live = resolveMember(memberId);
        const liveKey = memberIdOf(live) || memberId;
        const stub = live || { device_unique_id: memberId, name: memberId };
        if (ids.some((id) => id === liveKey || id === memberId || missionIdMatchesMember(id, stub))) {
          return {
            ...g,
            memberIds: ids.map((id) => (
              id === memberId || id === liveKey || missionIdMatchesMember(id, stub) ? liveKey : id
            ))
          };
        }
        return { ...g, memberIds: [...ids, liveKey] };
      });
    });
  };

  const onDropTo = (to) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = readDrag(e);
    if (!payload?.memberId) return;
    moveMember(payload.memberId, to);
    setDragPayload(null);
  };

  const allowDrop = (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) { /* ignore */ }
  };

  const addGroup = () => {
    const n = (missionGroups || []).length + 1;
    const id = 'g_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6);
    setGroups((prev) => [
      ...(prev || []),
      { id, name: '临时组 ' + n, collapsed: false, memberIds: [], color: COLORS[(n - 1) % COLORS.length], commsLinked: false, recordTracks: true }
    ]);
  };

  const renameGroup = (id) => {
    const name = String(draftName || '').trim();
    if (!name) {
      setEditingGroupId(null);
      return;
    }
    setGroups((prev) => (prev || []).map((g) => (g.id === id ? { ...g, name } : g)));
    setEditingGroupId(null);
  };

  const removeGroup = (id) => {
    setGroups((prev) => (prev || []).filter((g) => g.id !== id));
  };

  const toggleCollapse = (id) => {
    setGroups((prev) => (prev || []).map((g) => (g.id === id ? { ...g, collapsed: !g.collapsed } : g)));
  };

  const toggleCommsLinked = (id) => {
    setGroups((prev) => (prev || []).map((g) => (
      g.id === id ? { ...g, commsLinked: g.commsLinked !== true } : g
    )));
  };

  const toggleRecordTracks = (id) => {
    setGroups((prev) => (prev || []).map((g) => (
      g.id === id ? { ...g, recordTracks: g.recordTracks !== true } : g
    )));
  };

  const total = (missionMembers || []).length;
  const pulseSet = voicePulseIds instanceof Set
    ? voicePulseIds
    : new Set(Array.isArray(voicePulseIds) ? voicePulseIds : Object.keys(voicePulseIds || {}));

  const isPulsing = (m) => {
    const id = memberIdOf(m);
    if (!id) return false;
    if (pulseSet.has(id)) return true;
    if (m?.device_unique_id && pulseSet.has(m.device_unique_id)) return true;
    if (m?.name && pulseSet.has(m.name)) return true;
    return false;
  };
  const isMemberStreaming = (m) => {
    if (!m?.name || !streamingMembers?.has) return false;
    if (streamingMembers.has(m.name)) return true;
    // fallback: display-name key if ever stored differently
    try {
      const dn = getMemberDisplayName(m);
      return !!(dn && streamingMembers.has(dn));
    } catch (_) {
      return false;
    }
  };


  useEffect(() => {
    const member = chatMemberId ? resolveMember(chatMemberId) : null;
    onChatOpenChange?.(member || null);
    return () => { onChatOpenChange?.(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMemberId, onChatOpenChange]);

  const panelBox = {
    background: '#f7f9fc',
    border: '1px solid #e6ebf0',
    borderRadius: 8,
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  };

  const chatMember = chatMemberId ? resolveMember(chatMemberId) : null;

  const commandMenuPortal = commandMenu && typeof document !== 'undefined'
    ? ReactDOM.createPortal(
      (
        <div
          ref={commandMenuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: commandMenu.top,
            left: commandMenu.left,
            zIndex: 10050,
            minWidth: 120,
            background: '#fff',
            border: '1px solid #d0d7de',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(15,23,42,.18)',
            padding: 6
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              onSendLocationToMember?.(m);
            }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none',
              background: '#f8fafc', padding: '8px 10px', borderRadius: 6,
              cursor: 'pointer', fontSize: 13, color: '#0f172a', marginBottom: 4
            }}
          >
            发送位置
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              onSendMessageToMember?.(m);
            }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none',
              background: '#f8fafc', padding: '8px 10px', borderRadius: 6,
              cursor: 'pointer', fontSize: 13, color: '#0f172a', marginBottom: 4
            }}
          >
            发送消息
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              onFlyToMember?.(m);
            }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none',
              background: '#f8fafc', padding: '8px 10px', borderRadius: 6,
              cursor: 'pointer', fontSize: 13, color: '#0f172a', marginBottom: 4
            }}
          >
            定位到地图
          </button>
          {canEditNotes && (
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              setNoteEditor({ member: m, focusSection: 'remark' });
            }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none',
              background: '#f8fafc', padding: '8px 10px', borderRadius: 6,
              cursor: 'pointer', fontSize: 13, color: '#0f172a'
            }}
          >
            备注
          </button>
          )}
        </div>
      ),
      document.body
    )
    : null;

  return (
    <>
    <div style={{
      marginTop: 8,
      width: '100%',
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: 10,
      padding: 12
    }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch', minHeight: 200 }}>
        {/* Pending pool */}
        {poolMembers.length > 0 ? (
        <div
          onDragOver={allowDrop}
          onDrop={onDropTo('pool')}
          style={{
            ...panelBox,
            width: '100%',
            maxHeight: poolCollapsed ? undefined : 180,
            borderStyle: 'dashed',
            borderColor: '#c5d0dc'
          }}
        >
          <button
            type="button"
            onClick={() => setPoolCollapsed((v) => !v)}
            title="点击收起/展开待编组"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#555',
              marginBottom: poolCollapsed ? 0 : 6,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 0,
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <span style={{ width: 14, color: '#666' }}>{poolCollapsed ? '▸' : '▾'}</span>
            待编组 · {poolMembers.length}
          </button>
          {!poolCollapsed && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {poolMembers.length === 0 ? (
                <div style={{ fontSize: 11, color: '#8a93a0', padding: '12px 4px', lineHeight: 1.5 }}>
                  从「全部」勾选队员后点「加入任务中」
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {poolMembers.map((m) => {
                    const id = memberIdOf(m);
                    return (
                      <NameChip
                        key={id}
                        member={m}
                        onFlyTo={onFlyToMember}
                                  notesByKey={notesByKey}
                                  onOpenCommandMenu={openCommandMenuFor}
                        status={presenceOf(m, getMemberPresence)}
                        draggable={canEditBoard}
                        onDragStart={beginDrag(id, 'pool')}
                        onRemove={canEditBoard ? () => onRemoveMember?.(id) : undefined}
                        compact
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        ) : null}

        {/* Task groups */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginRight: 'auto' }}>任务分组</div>
            {canEditBoard && (
            <button
              type="button"
              onClick={addGroup}
              style={{
                height: 26,
                padding: '0 10px',
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #1976d2',
                background: '#fff',
                color: '#1976d2',
                cursor: 'pointer'
              }}
            >
              + 新建分组
            </button>
            )}
            {canEditBoard && (
            <button
              type="button"
              onClick={() => {
                if (!window.confirm('确认结束该任务？结束后将清除待编组与任务分组，收回语音权限并清理语音缓存；队员轨迹将保留。')) return;
                onEndMission?.();
              }}
              disabled={endMissionEnabled == null ? total === 0 : !endMissionEnabled}
              style={{
                height: 26,
                padding: '0 10px',
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #d0d7de',
                background: (endMissionEnabled == null ? total === 0 : !endMissionEnabled) ? '#f5f7fa' : '#fff',
                color: (endMissionEnabled == null ? total === 0 : !endMissionEnabled) ? '#aaa' : '#c62828',
                cursor: (endMissionEnabled == null ? total === 0 : !endMissionEnabled) ? 'not-allowed' : 'pointer'
              }}
              title="清空待编组与任务分组，收回语音权限，清理语音缓存；保留队员轨迹"
            >
              结束任务
            </button>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(missionGroups || []).length === 0 ? (
              <div style={{ fontSize: 11, color: '#8a93a0', padding: 8 }}>
                尚未建组。可先把人留在待编组，或新建后拖入。
              </div>
            ) : (
              (missionGroups || []).map((g) => {
                const color = g.color || COLORS[0];
                const ids = g.memberIds || [];
                const resolved = resolveGroupMembers(ids);
                const members = resolved.map((x) => x.member);
                return (
                  <div
                    key={g.id}
                    onDragOver={allowDrop}
                    onDrop={onDropTo(g.id)}
                    style={{
                      ...panelBox,
                      borderLeft: '3px solid ' + color,
                      background: '#fafbfd',
                      position: 'relative'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: g.collapsed ? 0 : 6 }}>
                      <button
                        type="button"
                        onClick={() => toggleCollapse(g.id)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: 0,
                          width: 16,
                          color: '#666',
                          fontSize: 12
                        }}
                      >
                        {g.collapsed ? '▸' : '▾'}
                      </button>
                      {editingGroupId === g.id ? (
                        <input
                          autoFocus
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          onBlur={() => renameGroup(g.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') renameGroup(g.id);
                            if (e.key === 'Escape') setEditingGroupId(null);
                          }}
                          style={{
                            flex: 1,
                            height: 24,
                            fontSize: 12,
                            border: '1px solid #1976d2',
                            borderRadius: 4,
                            padding: '0 6px',
                            minWidth: 0
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            if (!canEditBoard) return;
                            setEditingGroupId(g.id);
                            setDraftName(g.name || '');
                          }}
                          style={{
                            flex: 1,
                            textAlign: 'left',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#333',
                            padding: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title="点击改名"
                        >
                          {g.name || '未知'} · {members.length}
                        </button>
                      )}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={g.commsLinked === true}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleCommsLinked(g.id);
                        }}
                        title="开启后本组成员加入本任务群聊"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          flexShrink: 0,
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: '0 2px',
                          fontSize: 11,
                          color: g.commsLinked === true ? '#1976d2' : '#8a93a0',
                          fontWeight: g.commsLinked === true ? 600 : 400,
                          lineHeight: 1
                        }}
                      >
                        <span>通联</span>
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'relative',
                            width: 28,
                            height: 16,
                            borderRadius: 8,
                            background: g.commsLinked === true ? '#1976d2' : '#cfd5de',
                            transition: 'background 0.15s ease',
                            flexShrink: 0
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              top: 2,
                              left: g.commsLinked === true ? 14 : 2,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              background: '#fff',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                              transition: 'left 0.15s ease'
                            }}
                          />
                        </span>
                      </button>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={g.recordTracks === true}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleRecordTracks(g.id);
                        }}
                        title="开启后本组自动记录轨迹并续画（大空隙分段不硬连）"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          flexShrink: 0,
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: '0 2px',
                          fontSize: 11,
                          color: g.recordTracks === true ? '#2e7d32' : '#8a93a0',
                          fontWeight: g.recordTracks === true ? 600 : 400,
                          lineHeight: 1
                        }}
                      >
                        <span>轨迹</span>
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'relative',
                            width: 28,
                            height: 16,
                            borderRadius: 8,
                            background: g.recordTracks === true ? '#2e7d32' : '#cfd5de',
                            transition: 'background 0.15s ease',
                            flexShrink: 0
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              top: 2,
                              left: g.recordTracks === true ? 14 : 2,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              background: '#fff',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                              transition: 'left 0.15s ease'
                            }}
                          />
                        </span>
                      </button>
                      {canEditBoard && (
                      <button
                        type="button"
                        onClick={() => removeGroup(g.id)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#999',
                          cursor: 'pointer',
                          fontSize: 12,
                          padding: 0,
                          flexShrink: 0
                        }}
                        title="删除分组（成员回待编组）"
                      >
                        删组
                      </button>
                      )}
                    </div>
                    {!g.collapsed && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
                        {members.length === 0 ? (
                          <div style={{ fontSize: 11, color: '#8a93a0', padding: '4px 2px' }}>拖入队员</div>
                        ) : (
                          resolved.map(({ member: m, storedId }) => {
                            const id = memberIdOf(m) || storedId;
                            const rowKey = storedId || id;
                            const isExpanded = !!expandedMemberIds[rowKey];
                            return (
                              <div key={rowKey} style={{ position: 'relative' }}>
                                <GroupMemberRow
                                  member={m}
                                  status={presenceOf(m, getMemberPresence)}
                                  expanded={isExpanded}
                                  onToggleExpand={() => setExpandedMemberIds((prev) => ({
                                    ...prev,
                                    [rowKey]: !prev[rowKey]
                                  }))}
                                  showVoice
                                  voicePulsing={isPulsing(m)}
                                  onVoiceClick={() => setChatMemberId((prev) => (prev === id ? null : id))}
                                  isStreaming={isMemberStreaming(m)}
                                  onOpenLiveStream={openLiveStream}
                                  draggable={canEditBoard}
                                  onDragStart={beginDrag(storedId || id, g.id)}
                                  onRemove={canEditBoard ? () => moveMember(storedId || id, 'pool') : undefined}
                                  onFlyTo={onFlyToMember}
                                  notesByKey={notesByKey}
                                  onOpenCommandMenu={openCommandMenuFor}
                                />
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      {chatMember ? (
        <MissionMemberChatWindow
          member={chatMember}
          messages={getMessagesForMember?.(chatMember) || []}
          onClose={() => setChatMemberId(null)}
          onOutboundSent={(entry) => { onOutboundSent?.(entry); }}
          onMessageDeleted={(item) => { onMessageDeleted?.(item); }}
          getMap={getMap}
          getBasemapId={getBasemapId}
          historyEpoch={historyEpoch}
          isStreaming={isMemberStreaming(chatMember)}
          onOpenLiveStream={openLiveStream}
        />
      ) : null}
    </div>
      {commandMenuPortal}
      <MemberNoteEditorModal
        open={!!noteEditor}
        member={noteEditor?.member}
        note={
          noteEditor?.member
            ? (notesByKey && notesByKey[memberNoteKey(noteEditor.member)]) || null
            : null
        }
        focusSection={noteEditor?.focusSection || 'remark'}
        onClose={() => setNoteEditor(null)}
        onSave={onSaveMemberNote}
      />
    </>
  );
}
